import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import axios from 'axios';
import { X, Search, Package } from 'lucide-react';
import { overlayClose } from '../lib/overlayClose';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProductSearchModal = ({ open, onClose, onSelect }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${selectedIdx}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [selectedIdx, results]);

  useEffect(() => {
    if (open) {
      setSearch('');
      setResults([]);
      setSelectedIdx(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    if (!search) {
      setResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/products?search=${encodeURIComponent(search)}`, { withCredentials: true });
        setResults(data);
        setSelectedIdx(0);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') { onClose(); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, results.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); }
      else if (e.key === 'Enter' && results[selectedIdx]) { e.preventDefault(); onSelect(results[selectedIdx]); onClose(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, results, selectedIdx, onClose, onSelect]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 pt-20" {...overlayClose(onClose)}>
      <div className="bg-white rounded-lg p-6 w-[600px] max-w-[90vw] max-h-[70vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="product-search-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2"><Search size={20} /> Buscar Produto</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X size={20} /></button>
        </div>
        <Input
          ref={inputRef}
          placeholder="Digite o nome ou código do produto..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mb-4"
          data-testid="product-search-input"
        />
        <div className="flex-1 overflow-y-auto" ref={listRef} data-testid="search-results-list">
          {loading && <p className="text-center text-slate-500 py-4">Buscando...</p>}
          {!loading && search && results.length === 0 && (
            <p className="text-center text-slate-500 py-4">Nenhum produto encontrado</p>
          )}
          {results.map((p, idx) => (
            <div
              key={p.id}
              onClick={() => { onSelect(p); onClose(); }}
              onMouseEnter={() => setSelectedIdx(idx)}
              data-idx={idx}
              data-testid={`search-result-${p.id}`}
              className={`flex items-center justify-between p-3 rounded-md cursor-pointer border ${
                idx === selectedIdx ? 'bg-blue-50 border-blue-600' : 'border-transparent hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <Package size={20} className="text-slate-400" />
                <div>
                  <div className="font-medium text-slate-900">{p.name}</div>
                  <div className="text-xs text-slate-500">
                    {p.barcode && `Cod: ${p.barcode} | `}Estoque: {p.stock_quantity} {p.unit}
                  </div>
                </div>
              </div>
              <div className="text-blue-600 font-bold">R$ {p.price.toFixed(2)}</div>
            </div>
          ))}
        </div>
        <div className="text-xs text-slate-500 mt-3 text-center">
          ↑↓ navegar · Enter selecionar · ESC fechar
        </div>
      </div>
    </div>
  );
};
